import React, { useEffect, useState, useContext, useCallback, useMemo, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import UserLayout from '../../components/layouts/UserLayout';
import UserHeader from '../../components/header/UserHeader';
import { getMarketplaceProducts } from '../../services/productService';
import { AuthContext } from '../../contexts/AuthContext';
import { useAuthModal } from '../../contexts/AuthModalContext';
import cartService from '../../services/cartService';
import WishlistButton from '../../components/common/WishlistButton';
import VirtualTryOnModal from '../../components/common/VirtualTryOnModal';
import ProductCard from '../../components/common/ProductCard';
import { ProductGridSkeleton } from '../../components/common/SkeletonLoader';
import { ChevronLeft, SlidersHorizontal, Sparkles, ShoppingBag, LayoutGrid, X } from 'lucide-react';
import categoryService from '../../services/categoryService';
import { getBanners } from '../../services/bannerService';

// Enhanced terminal logging for production monitoring
const terminalLog = (action, status, data = null) => {
  const timestamp = new Date().toISOString();
  const logLevel = status === 'SUCCESS' ? '✅' : status === 'ERROR' ? '❌' : '🔄';
  
  console.log(`${logLevel} [PRODUCT-LIST-FRONTEND] ${timestamp} - ${action}`, data ? JSON.stringify(data, null, 2) : '');
};

// Level-4 names repeat across branches — "Dresses" hangs off both Women Fashion
// and Kids Fashion — so images are only ever keyed on the full path.
const level4Key = (l1, l2, l3, l4) => `${l1}>${l2}>${l3}>${l4}`;

// Fixed 72px track rather than responsive columns: this row renders inside
// MobileContainer's 480px shell, where sm:/md:/lg: would measure the window
// instead of the container and blow the row out.
const Level4Tile = ({ label, imageUrl, icon, active, onClick }) => {
  const [imgError, setImgError] = useState(false);
  const showImage = imageUrl && !imgError;

  return (
    <button onClick={onClick} className="flex-shrink-0 w-[72px] text-center">
      <div className={`w-16 h-16 rounded-full overflow-hidden mx-auto bg-gray-100 transition-all ${
        active ? 'ring-2 ring-orange-500 ring-offset-2' : ''
      }`}>
        {showImage ? (
          <img
            src={imageUrl}
            alt={label}
            className="w-full h-full object-cover object-top"
            loading="lazy"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center px-1">
            {icon || (
              <span className="text-[9px] font-semibold text-gray-400 leading-tight line-clamp-3">{label}</span>
            )}
          </div>
        )}
      </div>
      <p className={`text-[11px] text-center mt-1.5 line-clamp-2 leading-tight ${
        active ? 'font-bold text-gray-900' : 'font-medium text-gray-700'
      }`}>
        {label}
      </p>
    </button>
  );
};

const ProductListPage = () => {
  const { userAuth } = useContext(AuthContext);
  const { showAuthModal } = useAuthModal();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);

  // Get filter parameters from URL - support both legacy and new 4-level hierarchy
  const category = searchParams.get('category');
  const subcategory = searchParams.get('subCategory');
  const productCategory = searchParams.get('productCategory');
  const search = searchParams.get('search');

  // NEW: 4-level hierarchy parameters
  const categoryLevel1 = searchParams.get('categoryLevel1') || searchParams.get('level1');
  const categoryLevel2 = searchParams.get('categoryLevel2') || searchParams.get('level2');
  const categoryLevel3 = searchParams.get('categoryLevel3') || searchParams.get('level3');
  const categoryLevel4 = searchParams.get('categoryLevel4') || searchParams.get('level4');
  
  // Component state
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortBy] = useState('newest');
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [addingToCart, setAddingToCart] = useState({});
  
  // Virtual Try-On state
  const [showVirtualTryOn, setShowVirtualTryOn] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Level-4 tile imagery
  const [level4Banners, setLevel4Banners] = useState([]);
  const [level4BannersLoaded, setLevel4BannersLoaded] = useState(false);
  const [level4Fallbacks, setLevel4Fallbacks] = useState({});
  const [level4HasProducts, setLevel4HasProducts] = useState({});

  // Refs for preventing multiple calls
  const isMountedRef = useRef(true);
  const fetchingRef = useRef(false);
  const level4FallbackTriedRef = useRef(new Set());

  // Enhanced product fetching with comprehensive logging
  const fetchProducts = useCallback(async (page = 1) => {
    if (fetchingRef.current || !isMountedRef.current) return;
    
    fetchingRef.current = true;
    setLoading(true);
    
    try {
      terminalLog('PRODUCT_FETCH_START', 'PROCESSING', {
        category,
        subcategory,
        productCategory,
        search,
        categoryLevel1,
        categoryLevel2,
        categoryLevel3,
        categoryLevel4,
        page,
        sortBy,
        priceRange
      });

      console.log(`
🛍️ ===============================
   FETCHING FILTERED PRODUCTS
===============================
📂 Category: ${category || 'All'}
📁 Subcategory: ${subcategory || 'All'}
🏷️ Product Category: ${productCategory || 'All'}
🔍 Search: ${search || 'None'}
📊 Level 1: ${categoryLevel1 || 'All'}
📊 Level 2: ${categoryLevel2 || 'All'}
📊 Level 3: ${categoryLevel3 || 'All'}
📊 Level 4: ${categoryLevel4 || 'All'}
📄 Page: ${page}
🔢 Sort: ${sortBy}
💰 Price Range: ${priceRange.min || 0} - ${priceRange.max || '∞'}
👤 User: ${userAuth.user?.name || 'Guest'}
🕐 Time: ${new Date().toLocaleString()}
===============================`);

      // Build filter parameters - supporting both legacy and new 4-level hierarchy
      const params = {
        page,
        limit: 12,
        ...(category && { category }),
        ...(subcategory && { subCategory: subcategory }),
        ...(productCategory && { productCategory }),
        ...(search && { search }),
        ...(priceRange.min && { minPrice: priceRange.min }),
        ...(priceRange.max && { maxPrice: priceRange.max }),
        // NEW: 4-level hierarchy parameters
        ...(categoryLevel1 && { categoryLevel1 }),
        ...(categoryLevel2 && { categoryLevel2 }),
        ...(categoryLevel3 && { categoryLevel3 }),
        ...(categoryLevel4 && { categoryLevel4 })
      };
      
      // Add sorting
      switch (sortBy) {
        case 'price-low':
          params.sortBy = 'zammerPrice';
          params.sortOrder = 'asc';
          break;
        case 'price-high':
          params.sortBy = 'zammerPrice';
          params.sortOrder = 'desc';
          break;
        case 'popular':
          params.sortBy = 'views';
          params.sortOrder = 'desc';
          break;
        case 'newest':
        default:
          params.sortBy = 'createdAt';
          params.sortOrder = 'desc';
          break;
      }

      terminalLog('API_CALL_PARAMS', 'PROCESSING', params);
      console.log('📊 Final API Parameters:', params);
      
      const response = await getMarketplaceProducts(params);
      
      terminalLog('API_RESPONSE_RECEIVED', 'PROCESSING', {
        success: response.success,
        count: response.count,
        totalPages: response.totalPages,
        currentPage: response.currentPage
      });

      if (response.success && isMountedRef.current) {
        setProducts(response.data || []);
        setTotalPages(response.totalPages || 1);
        setCurrentPage(response.currentPage || 1);
        
        terminalLog('PRODUCT_FETCH_SUCCESS', 'SUCCESS', {
          productsCount: (response.data || []).length,
          totalPages: response.totalPages,
          currentPage: response.currentPage,
          filters: { category, subcategory, productCategory, search }
        });

        console.log(`
✅ ===============================
   PRODUCTS FETCHED SUCCESSFULLY!
===============================
📦 Products Found: ${(response.data || []).length}
📄 Current Page: ${response.currentPage || 1}
📋 Total Pages: ${response.totalPages || 1}
🎯 Filters Applied: ${Object.keys(params).length}
⏱️ Fetch Time: ${new Date().toLocaleString()}
===============================`);

        // Log first few products for debugging
        if (response.data && response.data.length > 0) {
          console.log('📦 First 3 Products:', response.data.slice(0, 3).map(p => ({
            id: p._id,
            name: p.name,
            price: p.zammerPrice,
            category: p.category,
            subCategory: p.subCategory
          })));
        }
        
      } else {
        terminalLog('PRODUCT_FETCH_FAILED', 'ERROR', {
          message: response.message,
          success: response.success
        });
        
        console.log(`❌ Product fetch failed: ${response.message}`);
        toast.error(response.message || 'Failed to fetch products');
      }
    } catch (error) {
      terminalLog('PRODUCT_FETCH_ERROR', 'ERROR', {
        error: error.message,
        stack: error.stack
      });
      
      console.error('❌ Error fetching products:', error);
      toast.error('Something went wrong while loading products');
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
      fetchingRef.current = false;
    }
  }, [category, subcategory, productCategory, search, categoryLevel1, categoryLevel2, categoryLevel3, categoryLevel4, sortBy, priceRange, userAuth.user?.name]);

  // Enhanced useEffect with proper cleanup
  useEffect(() => {
    isMountedRef.current = true;
    
    terminalLog('COMPONENT_MOUNT', 'PROCESSING', {
      urlParams: { category, subcategory, productCategory, search },
      userAuthenticated: userAuth.isAuthenticated,
      currentLocation: location.pathname + location.search
    });

    fetchProducts(1);

    return () => {
      isMountedRef.current = false;
      fetchingRef.current = false;
      terminalLog('COMPONENT_UNMOUNT', 'PROCESSING');
    };
  }, [fetchProducts]);

  // Enhanced Add to Cart with comprehensive logging
  const handleAddToCart = async (productId, productName) => {
    terminalLog('ADD_TO_CART_START', 'PROCESSING', {
      productId,
      productName,
      userAuth: {
        isAuthenticated: userAuth.isAuthenticated,
        hasToken: !!userAuth.token,
        userName: userAuth.user?.name
      }
    });

    // Check authentication
    if (!userAuth.isAuthenticated || !userAuth.token) {
      terminalLog('ADD_TO_CART_AUTH_FAILED', 'ERROR', {
        reason: 'User not authenticated',
        productId,
        redirectTo: '/user/login'
      });
      
      showAuthModal(() => handleAddToCart(productId, productName));
      return;
    }

    setAddingToCart(prev => ({ ...prev, [productId]: true }));
    
    try {
      terminalLog('CART_SERVICE_CALL', 'PROCESSING', {
        productId,
        quantity: 1,
        user: userAuth.user?.name
      });

      console.log(`
🛒 ===============================
   ADDING TO CART...
===============================
📦 Product: ${productName}
🔢 Product ID: ${productId}
📊 Quantity: 1
👤 User: ${userAuth.user?.name}
⏱️ Time: ${new Date().toLocaleString()}
===============================`);
      
      const response = await cartService.addToCart(productId, 1);
      
      terminalLog('CART_SERVICE_RESPONSE', 'PROCESSING', {
        success: response.success,
        message: response.message,
        requiresAuth: response.requiresAuth
      });
      
      if (response.success) {
        terminalLog('ADD_TO_CART_SUCCESS', 'SUCCESS', {
          productId,
          productName,
          cartData: response.data
        });

        console.log(`
✅ ===============================
   ADDED TO CART SUCCESSFULLY!
===============================
📦 Product: ${productName}
👤 User: ${userAuth.user?.name}
🛒 Cart Updated: ✅
🕐 Time: ${new Date().toLocaleString()}
===============================`);
        
        toast.success(`${productName} added to cart!`);
      } else {
        terminalLog('ADD_TO_CART_FAILED', 'ERROR', {
          productId,
          message: response.message,
          requiresAuth: response.requiresAuth
        });
        
        if (response.requiresAuth) {
          console.log('🔑 Re-authentication required...');
          showAuthModal(() => handleAddToCart(productId, productName));
        } else {
          toast.error(response.message || 'Failed to add to cart');
        }
      }
    } catch (error) {
      terminalLog('ADD_TO_CART_ERROR', 'ERROR', {
        productId,
        error: error.message,
        stack: error.stack
      });
      
      console.error('❌ Error adding to cart:', error);
      toast.error('Something went wrong while adding to cart');
    } finally {
      setAddingToCart(prev => ({ ...prev, [productId]: false }));
    }
  };

  // Handle page change
  const handlePageChange = (page) => {
    if (page !== currentPage && page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      fetchProducts(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Handle sort change
  const handleSortChange = (newSort) => {
    setSortBy(newSort);
    setCurrentPage(1);
  };

  // Handle price filter
  const applyPriceFilter = () => {
    setCurrentPage(1);
    fetchProducts(1);
    setShowFilters(false);
  };

  // Clear all filters
  const clearFilters = () => {
    setPriceRange({ min: '', max: '' });
    setSortBy('newest');
    setCurrentPage(1);
    navigate('/user/products');
  };

  // Get page title - support both legacy and 4-level hierarchy
  const getPageTitle = () => {
    if (search) return `Search Results: "${search}"`;
    // 4-level hierarchy title
    if (categoryLevel4) return categoryLevel4;
    if (categoryLevel3) return categoryLevel3;
    if (categoryLevel2) return categoryLevel2;
    if (categoryLevel1) return categoryLevel1;
    // Legacy title
    if (category && subcategory) return `${category} - ${subcategory}`;
    if (category) return `${category} Collection`;
    if (productCategory) return productCategory;
    return 'All Products';
  };

  // Level-4 values live under the current Level-3. Rendering them as tiles here
  // is what removes the extra navigation step: browsing to a Level-3 now lands
  // straight on products, and refining to "Cotton Kurta" filters this grid
  // instead of loading another category page.
  const level4Options = useMemo(
    () => ((categoryLevel1 && categoryLevel2 && categoryLevel3)
      ? categoryService.getLevel4Categories(categoryLevel1, categoryLevel2, categoryLevel3)
      : []),
    [categoryLevel1, categoryLevel2, categoryLevel3]
  );

  const hasLevel4Options = level4Options.length > 0;

  // One banner request covers every tile in the row. Keyed on the Level-1 alone
  // so walking between Level-3s under the same Level-1 reuses what we have.
  useEffect(() => {
    if (!categoryLevel1 || !hasLevel4Options) return;

    let cancelled = false;
    setLevel4BannersLoaded(false);

    getBanners({ level: 4, categoryLevel1 }).then((response) => {
      if (cancelled) return;
      setLevel4Banners(response?.success ? response.data || [] : []);
      setLevel4BannersLoaded(true);
    });

    return () => { cancelled = true; };
  }, [categoryLevel1, hasLevel4Options]);

  const level4Images = useMemo(() => {
    const byName = {};
    level4Banners.forEach((banner) => {
      if (
        banner.categoryLevel4 &&
        banner.categoryLevel1 === categoryLevel1 &&
        banner.categoryLevel2 === categoryLevel2 &&
        banner.categoryLevel3 === categoryLevel3
      ) {
        byName[banner.categoryLevel4] = banner.imageUrl;
      }
    });
    return byName;
  }, [level4Banners, categoryLevel1, categoryLevel2, categoryLevel3]);

  // A handful of leaf categories have no banner yet, so they borrow the first
  // product image instead. Gated on the banners having landed, otherwise every
  // tile would fire one of these on first paint and then throw it away.
  useEffect(() => {
    if (!level4BannersLoaded) return;

    const gaps = level4Options
      .map((opt) => opt.name || opt.id)
      .filter((name) => name && !level4Images[name] &&
        !level4FallbackTriedRef.current.has(level4Key(categoryLevel1, categoryLevel2, categoryLevel3, name)));

    if (gaps.length === 0) return;
    gaps.forEach((name) =>
      level4FallbackTriedRef.current.add(level4Key(categoryLevel1, categoryLevel2, categoryLevel3, name)));

    let cancelled = false;
    Promise.all(gaps.map(async (name) => {
      const response = await getMarketplaceProducts({
        limit: 1,
        categoryLevel1,
        categoryLevel2,
        categoryLevel3,
        categoryLevel4: name
      });
      return {
        key: level4Key(categoryLevel1, categoryLevel2, categoryLevel3, name),
        image: response?.data?.[0]?.images?.[0] || null,
        // Separate from the image: a leaf can hold products that have no photo,
        // and that still counts as stocked for ordering purposes.
        hasProducts: (response?.data?.length || 0) > 0
      };
    })).then((entries) => {
      if (cancelled) return;
      setLevel4Fallbacks((prev) => ({
        ...prev,
        ...Object.fromEntries(entries.map(({ key, image }) => [key, image]))
      }));
      setLevel4HasProducts((prev) => ({
        ...prev,
        ...Object.fromEntries(entries.map(({ key, hasProducts }) => [key, hasProducts]))
      }));
    });

    return () => { cancelled = true; };
  }, [level4BannersLoaded, level4Options, level4Images, categoryLevel1, categoryLevel2, categoryLevel3]);

  const level4ImageFor = (name) =>
    level4Images[name] || level4Fallbacks[level4Key(categoryLevel1, categoryLevel2, categoryLevel3, name)] || null;

  // Leaves a buyer can actually shop come first; the ones nobody has listed in
  // yet sink to the end of the row rather than occupying the first taps.
  //
  // /api/banners drops any banner whose productCount is 0, so a leaf that still
  // has a banner here is known-stocked. Everything else is answered by the
  // fallback lookup above. Sorting is stable, so taxonomy order survives within
  // each group, and the row only reorders once those lookups land.
  const orderedLevel4Options = useMemo(() => {
    const isStocked = (opt) => {
      const name = opt.name || opt.id;
      if (!name) return false;
      if (level4Images[name]) return true;
      return level4HasProducts[level4Key(categoryLevel1, categoryLevel2, categoryLevel3, name)] === true;
    };

    return [...level4Options].sort((a, b) => Number(isStocked(b)) - Number(isStocked(a)));
  }, [level4Options, level4Images, level4HasProducts, categoryLevel1, categoryLevel2, categoryLevel3]);

  const applyLevel4 = (value) => {
    const next = new URLSearchParams(searchParams);
    // Tapping the active tile clears it, so the buyer can widen again without
    // using the back button.
    if (!value || value === categoryLevel4) {
      next.delete('categoryLevel4');
      next.delete('level4');
    } else {
      next.set('categoryLevel4', value);
      next.delete('level4');
    }
    // This page reads its filters straight off location.search, so navigating
    // is what re-runs the fetch. replace:true keeps tile taps out of history —
    // otherwise Back walks through every refinement instead of leaving the page.
    navigate(`/user/products?${next.toString()}`, { replace: true });
  };

  return (
    <UserLayout>
      <UserHeader />
      <div className="min-h-screen bg-gray-50 pb-20 lg:pb-0">
        {/* Page Header */}
        <div className="bg-white border-b border-gray-200">
          <div className="px-4">
            <div className="flex items-center justify-between py-5">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate(-1)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ChevronLeft className="w-5 h-5 text-gray-600" strokeWidth={2} />
                </button>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">{getPageTitle()}</h1>
                </div>
              </div>
              <button
                onClick={() => setShowFilters(true)}
                className="flex items-center gap-2 px-4 py-2 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-lg font-medium transition-colors border border-gray-200"
              >
                <SlidersHorizontal className="w-4 h-4" strokeWidth={2} />
                <span className="hidden sm:inline">Filters</span>
              </button>
            </div>
          </div>
        </div>

        {/* Level-4 refinement tiles */}
        {hasLevel4Options && (
          <div className="bg-white border-b border-gray-100">
            <div className="flex gap-4 overflow-x-auto px-4 py-3 scrollbar-hide">
              <Level4Tile
                label="All"
                icon={<LayoutGrid className="w-6 h-6 text-gray-400" strokeWidth={1.5} />}
                active={!categoryLevel4}
                onClick={() => applyLevel4(null)}
              />
              {orderedLevel4Options.map((opt) => {
                const value = opt.id || opt.name;
                const label = opt.name || value;
                return (
                  <Level4Tile
                    key={value}
                    label={label}
                    imageUrl={level4ImageFor(label)}
                    active={categoryLevel4 === value}
                    onClick={() => applyLevel4(value)}
                  />
                );
              })}
            </div>
          </div>
        )}

        {/* Sort & Results Bar */}
        <div className="bg-white border-b border-gray-200">
          <div className="px-4 py-3">
            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-600">
                <span className="font-semibold text-gray-900">{products.length}</span> products
                {currentPage > 1 && (
                  <span className="ml-2 text-gray-500">• Page {currentPage} of {totalPages}</span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <select
                  value={sortBy}
                  onChange={(e) => handleSortChange(e.target.value)}
                  className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 bg-white transition-all"
                >
                  <option value="newest">Newest First</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                  <option value="popular">Most Popular</option>
                </select>
                {(category || subcategory || productCategory || search || priceRange.min || priceRange.max || categoryLevel1 || categoryLevel2 || categoryLevel3 || categoryLevel4) && (
                  <button
                    onClick={clearFilters}
                    className="text-orange-600 hover:text-orange-700 text-sm font-medium transition-colors"
                  >
                    Clear All
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="px-4 py-6">
          {loading ? (
            <ProductGridSkeleton count={10} />
          ) : products.length > 0 ? (
            <>
              {/* Products Grid */}
              <div className="grid grid-cols-2 gap-3 mb-8">
                {products.map(product => (
                  <ProductCard
                    key={product._id}
                    product={product}
                    onAddToCart={handleAddToCart}
                    onTryOn={(product) => {
                      setSelectedProduct(product);
                      setShowVirtualTryOn(true);
                    }}
                    isAddingToCart={addingToCart[product._id]}
                  />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center">
                  <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg p-1 shadow-sm">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                        currentPage === 1
                          ? 'bg-gray-50 text-gray-300 cursor-not-allowed'
                          : 'hover:bg-gray-50 text-gray-700'
                      }`}
                    >
                      Previous
                    </button>

                    {/* Page Numbers */}
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                            currentPage === pageNum
                              ? 'bg-orange-600 text-white'
                              : 'hover:bg-gray-50 text-gray-700'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}

                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                        currentPage === totalPages
                          ? 'bg-gray-50 text-gray-300 cursor-not-allowed'
                          : 'hover:bg-gray-50 text-gray-700'
                      }`}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShoppingBag className="w-8 h-8 text-gray-400" strokeWidth={1.5} />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">No Products Found</h3>
              <p className="text-sm text-gray-500 mb-6 max-w-md mx-auto">
                {category || subcategory || search
                  ? "We couldn't find any products matching your criteria. Try adjusting your filters."
                  : "No products are available at the moment. Please check back later."
                }
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                {(category || subcategory || productCategory || search || priceRange.min || priceRange.max || categoryLevel1 || categoryLevel2 || categoryLevel3 || categoryLevel4) && (
                  <button
                    onClick={clearFilters}
                    className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-2.5 rounded-lg font-semibold transition-colors"
                  >
                    Clear All Filters
                  </button>
                )}
                <Link
                  to="/user/dashboard"
                  className="inline-block bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 px-6 py-2.5 rounded-lg font-semibold transition-colors"
                >
                  Browse All Categories
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* Filter Modal — z-[60] to clear the z-50 bottom nav; see MyOrdersPage
            for why an equal z-index loses to it. */}
        {showFilters && (
          <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="absolute inset-0 bg-black/50" onClick={() => setShowFilters(false)}></div>
            <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full max-w-md border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-bold text-gray-900">Filter Products</h2>
                  <button
                    onClick={() => setShowFilters(false)}
                    className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-5">
                {/* Price Range Filter */}
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">Price Range</label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1.5">Min Price</label>
                      <input
                        type="number"
                        placeholder="₹0"
                        value={priceRange.min}
                        onChange={(e) => setPriceRange(prev => ({ ...prev, min: e.target.value }))}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1.5">Max Price</label>
                      <input
                        type="number"
                        placeholder="₹10000"
                        value={priceRange.max}
                        onChange={(e) => setPriceRange(prev => ({ ...prev, max: e.target.value }))}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 transition-all"
                      />
                    </div>
                  </div>
                </div>

                {/* Sort Options */}
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">Sort By</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 bg-white transition-all"
                  >
                    <option value="newest">Newest First</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                    <option value="popular">Most Popular</option>
                  </select>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="px-6 py-4 border-t border-gray-200">
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setShowFilters(false)}
                    className="bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 py-2.5 rounded-lg font-semibold transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={applyPriceFilter}
                    className="bg-orange-600 hover:bg-orange-700 text-white py-2.5 rounded-lg font-semibold transition-colors"
                  >
                    Apply Filters
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>

      {/* Virtual Try-On Modal */}
      <VirtualTryOnModal
        isOpen={showVirtualTryOn}
        onClose={() => {
          setShowVirtualTryOn(false);
          setSelectedProduct(null);
        }}
        product={selectedProduct}
        onTryOnComplete={(result) => {
          console.log('Virtual try-on completed:', result);
          toast.success('Virtual try-on completed! Check out the result.');
        }}
      />
    </UserLayout>
  );
};

export default ProductListPage;